import { useMutation } from "@tanstack/react-query";

import { autoId } from "~/utils/auto-id";
import { basketActions } from "~/providers/store/basket";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { getAuth, isProfileAuth } from "~/providers/store/auth";
import {
  createOrderV4Async,
  type CreateOrderV4AsyncArgs
} from "~/api/orders/requests";
import { useDeviceData } from "~/providers/app";
import { getUserIdentifier } from "~/providers/store/employee";
import { CREATE_ORDER_V4_MUTATION_KEY } from "~/api/orders/hooks/use-create-order";
import { DisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";
import { useInvalidateProducts } from "~/api/products/hooks/use-invalidate-products";

import { type CreateCardRequest } from "./useOrderCardCreateV3";

function extractDiscounts(
  item: CreateCardRequest["body"]["basket"][number]
): CreateOrderV4AsyncArgs["basket"][number]["discounts"] {
  if (item.discount) {
    return [
      {
        type: item.discount.type,
        id: item.discount.id,
        name: item.discount.name,
        percentage: item.discount.percentage,
        allowanceType: item.discount.allowanceType,
        unitDiscountInclVat: item.discount.amount
      }
    ];
  }

  if (item.unitDiscountedPrice) {
    return [
      {
        type: "discount-line" as const,
        id: "discount-line",
        name: "POS Line Discount",
        percentage: null,
        allowanceType: null,
        unitDiscountInclVat: item.unitDiscountedPrice
      }
    ];
  }

  return [];
}

export function useOrderCardCreateV4() {
  const { data: till } = useCurrentTill();
  const invalidateProducts = useInvalidateProducts();

  const { os, osVersion } = useDeviceData();

  return useMutation({
    mutationKey: [CREATE_ORDER_V4_MUTATION_KEY],
    mutationFn: async ({
      cardAmountUnitInclVat,
      balanceAmountUnitInclVat,
      info,
      shopperName,
      paymentId,
      ...body
    }: Omit<
      CreateOrderV4AsyncArgs,
      | "basket"
      | "info"
      | "userIdentifier"
      | "payments"
      | "requestId"
      | "profileId"
      | "deviceInfo"
      | "paidWithSaldoUnit"
    > & {
      basket: CreateCardRequest["body"]["basket"];
      shopperName: string | null;
      /** Amount to charge on card in unit (incl. VAT) */
      cardAmountUnitInclVat: number;
      /** Amount to be taken from balance/credit, in unit (incl. VAT) */
      balanceAmountUnitInclVat: number;
      paymentId: string;
      info?: {
        textInput: Record<string, string>;
      };
    }) => {
      basketActions.setCardPaymentInProgress(true);

      if (!till?.freedompayTerminalId) {
        throw new Error("Missing freedompay terminal id configuration");
      }

      if (!os || !osVersion) throw new Error("OS or OS Version not provided");

      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      const userIdentifier = getUserIdentifier();

      const transformedBasket: CreateOrderV4AsyncArgs["basket"] =
        body.basket.map((item) => ({
          type:
            item.type === "custom" || item.type === "variant-weight"
              ? "regular"
              : item.type,
          productLineId: item.productLineId,
          id: item.id,
          category: item.category,
          unitPriceExclVat: item.unitPrice, // Map unitPrice to unitPriceExclVat
          name: item.name,
          amount: item.amount,
          version: "v1" as const,
          taxRate: {
            id: item.taxRateId,
            name: item.taxRateName,
            vatRate: item.vatRate
          },
          discounts: extractDiscounts(item),
          stampCardRuleId: item.stampCardRuleId,
          deposit: item.deposit,
          ...((item.type === "variant" || item.type === "variant-weight") &&
            item.options && {
              options: item.options.map((option) => ({
                id: "id" in option ? option.id : autoId(),
                choices: option.choices
                  .filter((choice) => choice.amount > 0)
                  .map((choice) => ({
                    id: choice.id,
                    amount: choice.amount,
                    name: choice.name ?? "",
                    // The v1 schema strips unknown keys server-side, so the
                    // choice price must travel as unitPriceExclVat or the
                    // recomputed order total misses it.
                    ...("unitPrice" in choice && {
                      unitPriceExclVat: choice.unitPrice ?? 0
                    }),
                    ...("weight" in choice && { weight: choice.weight }),
                    ...("weightUnit" in choice && {
                      weightUnit: choice.weightUnit
                    }),
                    ...("weightDecimals" in choice && {
                      weightDecimals: choice.weightDecimals
                    }),
                    ...(choice.deposit && { deposit: choice.deposit })
                  }))
              }))
            })
        }));

      const payments: CreateOrderV4AsyncArgs["payments"] = [];

      if (balanceAmountUnitInclVat && balanceAmountUnitInclVat > 0) {
        payments.push({
          paymentId: `credit-${autoId()}`,
          paymentMethod: `credit`,
          unitRequestedAmountInclVat: balanceAmountUnitInclVat
        });
      }

      // Primary card payment leg (required for this flow)
      payments.push({
        paymentId,
        paymentMethod: "card",
        unitRequestedAmountInclVat: cardAmountUnitInclVat
      });

      const res = await createOrderV4Async({
        orderId: body.orderId,
        tillId: auth.tillId,
        basket: transformedBasket,
        payments,
        profileId: auth.profileId,
        requestId: "N/A",
        info: {
          customShopperName: shopperName ?? undefined,
          textInputs: info?.textInput
            ? Object.entries(info.textInput).map(([id, value]) => ({
                id,
                value
              }))
            : undefined
        },
        deviceInfo: { os, osVersion },
        userIdentifier:
          userIdentifier &&
          (userIdentifier.type === "child-id" ||
            userIdentifier.type === "group-name")
            ? {
                type: userIdentifier.type,
                value: userIdentifier.value
              }
            : null
      });

      basketActions.setCardPaymentInProgress(false);

      return res;
    },
    onError: () => {
      invalidateProducts(null);
    }
  });
}
